/**
 * Session Manager V2 for TrustIQ
 * Implements session-per-login with "Start New Client" workflow
 *
 * Key features:
 * - One active session per user login
 * - Session validated server-side via Cognito
 * - "Start New Client" creates fresh session
 * - Logout clears all session state
 */

import { fetchAuthSession } from 'aws-amplify/auth';

// API endpoints for session management
// Using the same API Gateway as document creation
// Use environment variable, fallback to production endpoint
const SESSION_API_BASE = import.meta.env.VITE_DOC_CREATION_API || 'https://xg5e7udwse.execute-api.us-east-2.amazonaws.com';

const SESSION_ENDPOINTS = {
  create: `${SESSION_API_BASE}/sessions`,
  end: `${SESSION_API_BASE}/sessions/end`,
  active: `${SESSION_API_BASE}/sessions/active`
};

// In-memory cache
let cachedSessionId: string | null = null;

const CLIENT_STORAGE_KEYS = [
  'currentDocSessionId',
  'sessionTimestamp',
  'aiDraftingSessionId',
  'aiDraftingSessionTimestamp'
];

const CLIENT_STORAGE_PREFIXES = [
  'aiDraftingHistory_'
];

function clearClientStorage(): void {
  CLIENT_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));

  Object.keys(localStorage).forEach(key => {
    if (CLIENT_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix))) {
      localStorage.removeItem(key);
    }
  });
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
  } catch {
    // no session; request will 401 at the API
  }
  return headers;
}

/**
 * Get the current active session ID
 * Fetches from server if not cached
 */
export async function getActiveSessionId(): Promise<string> {
  if (cachedSessionId) {
    console.log('[SessionManager] Returning cached session:', cachedSessionId);
    return cachedSessionId;
  }

  try {
    // Check if active session exists. The API derives user identity from Cognito.
    const activeResponse = await fetch(SESSION_ENDPOINTS.active, {
      method: 'GET',
      headers: await authHeaders()
    });

    if (activeResponse.ok) {
      const data = await activeResponse.json();
      cachedSessionId = data.sessionId;
      console.log('[SessionManager] Retrieved active session:', cachedSessionId);
      return cachedSessionId;
    }

    // No active session exists - create new one
    if (activeResponse.status === 404) {
      console.log('[SessionManager] No active session found, creating new one');
      const createResponse = await fetch(SESSION_ENDPOINTS.create, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          clientName: 'New Client'
        })
      });

      if (!createResponse.ok) {
        throw new Error(`Failed to create session: ${createResponse.statusText}`);
      }

      const data = await createResponse.json();
      cachedSessionId = data.sessionId;
      console.log('[SessionManager] Created new session:', cachedSessionId);
      return cachedSessionId;
    }

    throw new Error(`Failed to get active session: ${activeResponse.statusText}`);

  } catch (error) {
    console.error('[SessionManager] Error getting active session:', error);
    throw error;
  }
}

/**
 * Start a new client session
 * Ends current session and creates a fresh one
 */
export async function startNewClient(): Promise<string> {
  try {
    console.log('[SessionManager] Starting new client session...');

    // End current session
    try {
      await fetch(SESSION_ENDPOINTS.end, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({})
      });
      console.log('[SessionManager] Ended current session');
    } catch (error) {
      console.warn('[SessionManager] Error ending session (may not exist):', error);
    }

    // Clear cache
    cachedSessionId = null;

    // Create new session (force new)
    const createResponse = await fetch(SESSION_ENDPOINTS.create, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({
        forceNew: true,
        clientName: 'New Client'
      })
    });

    if (!createResponse.ok) {
      throw new Error(`Failed to create new session: ${createResponse.statusText}`);
    }

    const data = await createResponse.json();
    cachedSessionId = data.sessionId;

    clearClientStorage();

    console.log('[SessionManager] Started new client session:', cachedSessionId);
    return cachedSessionId;

  } catch (error) {
    console.error('[SessionManager] Error starting new client:', error);
    throw error;
  }
}

/**
 * Clear session on logout
 * Ends server session and clears all local state
 */
export async function clearSessionOnLogout(): Promise<void> {
  try {
    console.log('[SessionManager] Clearing session on logout...');

    // End session on server. The API derives user identity from Cognito.
    try {
      await fetch(SESSION_ENDPOINTS.end, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({})
      });
      console.log('[SessionManager] Server session ended');
    } catch (error) {
      console.warn('[SessionManager] Error ending server session:', error);
    }

    // Clear local cache
    cachedSessionId = null;

    // Clear localStorage
    localStorage.clear();

    console.log('[SessionManager] Session cleared successfully');

  } catch (error) {
    console.error('[SessionManager] Error clearing session:', error);
    // Don't throw - we want logout to proceed even if this fails
  }
}

/**
 * Reset local session cache
 * Use this when you know the server session has changed
 */
export function resetSessionCache(): void {
  cachedSessionId = null;
  console.log('[SessionManager] Session cache reset');
}

/**
 * Get cached session ID without fetching from server
 * Returns null if no session is cached
 */
export function getCachedSessionId(): string | null {
  return cachedSessionId;
}
